import { useState } from 'react';
import {
  Stack,
  Title,

  Box,
  Space,
  Center,
  ScrollArea,
} from '@mantine/core';
import { useApp } from '../../context/AppContext';
import { primaryColor } from '../../constants/theme';
import { useParams } from 'react-router-dom';
import { NavigationWrapper } from '../../components/navigation/NavigationWrapper';
import { useQuery } from '@tanstack/react-query';
import { getBookingById } from '../../services/BookingService';
import { IconAlertTriangle } from '@tabler/icons-react';

import { SingleBookingSkeletonLoader } from './components/bookings/SingleBooking/SingleBookingSkeletonLoader';
import { SingleBookingHeaderCard } from './components/bookings/SingleBooking/SingleBookingHeaderCard';
import { SingleBookingStatusCard } from './components/bookings/SingleBooking/SingleBookingStatusCard';
import { SingleBookingActionsCard } from './components/bookings/SingleBooking/SingleBookingActionsCard';
import { SingleBookingFeedbackCard } from './components/bookings/SingleBooking/SingleBookingFeedbackCard';


export const SingleBookingPage = () => {
  const { id } = useParams<{ id: string }>();
  const { profile, token } = useApp();
  const [disputeOpened, setDisputeOpened] = useState(false);

  const { data: bookingsResponse, isLoading, isError, refetch } = useQuery({
    enabled: !!token && !!id,
    queryKey: ['booking', id],
    queryFn: () => getBookingById(id!, token),
  });

  if (isLoading) {
    return (
      <NavigationWrapper>
        <Box data-testid="single-booking-skeleton">
          <SingleBookingSkeletonLoader />
        </Box>
      </NavigationWrapper>
    );
  }

  if (isError || !bookingsResponse || !bookingsResponse.bookings || bookingsResponse.bookings.length === 0) {
    return (
      <NavigationWrapper>
        <Center h={400} data-testid="single-booking-error">
          <Stack align="center">
            <IconAlertTriangle size={40} color={primaryColor} />
            <Title order={5}>Could not find booking</Title>
          </Stack>
        </Center>
      </NavigationWrapper>
    );
  }

  const groupedBookings = bookingsResponse.bookings;
  const booking = groupedBookings[0];
  const currentProfile = profile || { id: booking.userId };

  const bookingType = booking.userId === currentProfile.id ? 'outgoing' : 'incoming';

  const totalCost = booking.rate + booking.bookingFee;
  const isFinished = new Date() > new Date(booking.endIso);
  const { scrollAreaHeight, isTabletAndBelow } = useApp();

  return (
    <NavigationWrapper>
      <ScrollArea h={isTabletAndBelow ? scrollAreaHeight : 'auto'}>
        <Box p='lg' mx='auto' bg='white' data-testid="single-booking-page-content" w={isTabletAndBelow ? '100%' : '1200px'}>
          <Box data-testid="single-booking-header">
            <SingleBookingHeaderCard
              bookingType={bookingType}
              booking={booking}
              profileId={currentProfile.id}
              groupedBookings={groupedBookings}
            />
          </Box>

          <Space h='sm' />

          <Box data-testid="single-booking-status">
            <SingleBookingStatusCard
              booking={booking}
              totalCost={totalCost}
              isFinished={isFinished}
            />
          </Box>

          <Space h='sm' />

          <Box data-testid="single-booking-actions">
            <SingleBookingActionsCard
              booking={booking}
              profileId={currentProfile.id}
              groupedBookings={groupedBookings}
              bookingType={bookingType}
              totalCost={totalCost}
              isFinished={isFinished}
              refreshBookings={refetch}
              token={token}
              reloadOnSuccess={true}
            />
          </Box>

          {bookingType === 'outgoing' && isFinished && !booking.isCancelled && (booking.paymentStatus === 'succeeded' || totalCost === 0) && (
            <>
              <Space h='md' />
              <Box data-testid="single-booking-feedback">
                <SingleBookingFeedbackCard
                  booking={booking}
                  disputeOpened={disputeOpened}
                  setDisputeOpened={setDisputeOpened}
                />
              </Box>
            </>
          )}
        </Box>

        <Space h='md' />
      </ScrollArea>

    </NavigationWrapper>
  );
};
